import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { toFraction } from '../utils/quantityFormat';
import {
  MEAL_TYPE_CATEGORIES,
  MEAL_TYPE_LABELS,
  COOKING_METHODS,
  COOKING_METHOD_LABELS,
  PROTEINS,
  PROTEIN_LABELS,
  MAX_OTHER_TAGS,
  itemProteins,
  itemCookingMethods,
  otherTagsFrom,
  normalizeCookingMethod,
  isMealTypeTag,
  isProteinTag,
  isReservedFilterTag
} from '../constants/menuTaxonomy';
import './MenuUploadForm.css';

/** "1/3" or "1 1/2" -> { numerator, denominator } for the fraction inputs. */
const parseFractionParts = (display) => {
  const match = String(display || '').trim().match(/^(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)$/);
  if (!match) return null;

  const whole = match[1] ? parseInt(match[1], 10) : 0;
  const denominator = parseInt(match[3], 10);
  if (!denominator) return null;

  return { numerator: whole * denominator + parseInt(match[2], 10), denominator };
};

const INITIAL_FORM_STATE = {
  title: '',
  description: '',
  detailedDescription: '',
  cuisine: '',
  prepTime: '',
  cookTime: '',
  totalTime: '',
  servings: 1,
  contentUrl: '',
  equipment: [],
  ingredients: [{
    quantity: '1',
    unit: '',
    name: '',
    prepNotes: '',
    isSpice: false,
    isOil: false,
    useFraction: false,
    numerator: '',
    denominator: ''
  }],
  instructions: [''],
  nutrition: {
    Calories: '0',
    Protein: '0',
    Carbohydrates: '0',
    Fat: '0',
    Fiber: '0',
    Sodium: '0'
  },
  image: '',
  tags: [],
  dietary: [],
  categories: [],
  proteins: []
};

// Section configuration for collapsible behavior
const SECTION_CONFIG = {
  basicInfo: { title: 'Basic Information', fields: ['title', 'description', 'detailedDescription', 'cuisine', 'contentUrl', 'image'] },
  timing: { title: 'Timing & Servings', fields: ['prepTime', 'cookTime', 'totalTime', 'servings'] },
  ingredients: { title: 'Ingredients', fields: ['ingredients'] },
  instructions: { title: 'Instructions', fields: ['instructions'] },
  equipment: { title: 'Kitchen Equipment', fields: ['equipment'] },
  tags: { title: 'Tags', fields: ['tags'] },
  nutrition: { title: 'Nutrition Information', fields: ['nutrition'] }
};

// Helper function to parse time strings into minutes
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const str = String(timeStr).toLowerCase().trim();
  
  // Try to match patterns like "15 minutes", "1 hour", "1 hour 30 minutes", "1h 30m", "90 min"
  let totalMinutes = 0;
  
  // Match hours
  const hourMatch = str.match(/(\d+)\s*(hour|hr|h)/i);
  if (hourMatch) {
    totalMinutes += parseInt(hourMatch[1]) * 60;
  }
  
  // Match minutes
  const minMatch = str.match(/(\d+)\s*(minute|min|m)(?!onth)/i);
  if (minMatch) {
    totalMinutes += parseInt(minMatch[1]);
  }
  
  // If no units found, try to parse as just a number (assume minutes)
  if (totalMinutes === 0 && /^\d+$/.test(str.trim())) {
    totalMinutes = parseInt(str.trim());
  }
  
  return totalMinutes;
};

// Helper function to extract numeric minutes from a time string for form input
// Converts "10 minutes", "1 hour 30 minutes", etc. to just the number of minutes
const extractMinutesAsString = (timeStr) => {
  if (!timeStr) return '';
  const str = String(timeStr).trim();
  
  // If already just a number, return as-is
  if (/^\d+$/.test(str)) {
    return str;
  }
  
  // Parse the time string to get total minutes
  const minutes = parseTimeToMinutes(str);
  return minutes > 0 ? String(minutes) : '';
};

// Helper function to format minutes to a readable string
const formatMinutesToTime = (minutes) => {
  if (!minutes || minutes <= 0) return '';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins} minutes`;
  } else if (mins === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  } else {
    const hourText = hours === 1 ? '1 hour' : `${hours} hours`;
    return `${hourText} ${mins} minutes`;
  }
};

function toggleListValue(list, value) {
  const current = list || [];
  return current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value];
}

const MenuForm = ({ onSuccess, onCancel, initialData = null, isAdmin = false, isPersonal = false }) => {
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [cuisineOptions, setCuisineOptions] = useState([]);
  const [showNewCuisineInput, setShowNewCuisineInput] = useState(false);
  const [timeInputErrors, setTimeInputErrors] = useState({ prepTime: '', cookTime: '' });
  const [customTagInput, setCustomTagInput] = useState('');
  const [formData, setFormData] = useState(() => {
    if (!initialData) {
      return INITIAL_FORM_STATE;
    }

    // Parse prep/cook times - extract just the numeric minutes from strings like "10 minutes"
    const prepTimeMinutes = extractMinutesAsString(initialData.prepTime);
    const cookTimeMinutes = extractMinutesAsString(initialData.cookTime);
    const totalMinutes = (parseInt(prepTimeMinutes) || 0) + (parseInt(cookTimeMinutes) || 0);

    const formattedData = {
      ...INITIAL_FORM_STATE,
      ...initialData,
      // Override time fields with numeric-only values
      prepTime: prepTimeMinutes,
      cookTime: cookTimeMinutes,
      totalTime: totalMinutes > 0 ? formatMinutesToTime(totalMinutes) : (initialData.totalTime || ''),
      cuisine: initialData.cuisine || '',
      dietary: initialData.dietary || [],
      categories: (initialData.categories?.length
        ? initialData.categories
        : MEAL_TYPE_CATEGORIES.filter((category) =>
          (initialData.tags || []).some((tag) => String(tag).toLowerCase() === category)
        )),
      proteins: (initialData.proteins?.length
        ? initialData.proteins
        : itemProteins(initialData)),
      tags: (initialData.tags || []).filter((tag) =>
        !isMealTypeTag(tag) && !isProteinTag(tag)
      ),
      ingredients: initialData.ingredients?.map(ing => {
        // A saved fraction reopens in fraction mode, so editing a recipe does
        // not silently rewrite "1/3" as 0.333.
        const fraction = parseFractionParts(ing.quantityDisplay);
        return {
          quantity: ing.quantity || '1',
          unit: ing.unit || '',
          name: ing.name || '',
          prepNotes: ing.prepNotes || '',
          isSpice: !!ing.isSpice,
          isOil: !!ing.isOil,
          useFraction: Boolean(fraction),
          numerator: fraction ? String(fraction.numerator) : '',
          denominator: fraction ? String(fraction.denominator) : ''
        };
      }) || INITIAL_FORM_STATE.ingredients,
      instructions: initialData.instructions?.length ? initialData.instructions : INITIAL_FORM_STATE.instructions,
      nutrition: {
        ...INITIAL_FORM_STATE.nutrition,
        ...(initialData.nutrition || {}),
        Calories: initialData.nutrition?.Calories || '',
        Protein: initialData.nutrition?.Protein || '',
        Carbohydrates: initialData.nutrition?.Carbohydrates || '',
        Fat: initialData.nutrition?.Fat || '',
        Fiber: initialData.nutrition?.Fiber || '',
        Sodium: initialData.nutrition?.Sodium || ''
      }
    };

    return formattedData;
  });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [urlError, setUrlError] = useState(false);
  
  // New state for enhanced UX
  const [saveStatus, setSaveStatus] = useState(''); // '', 'validating', 'uploading', 'generating', 'saving'
  const [showFloatingSave, setShowFloatingSave] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorSection, setErrorSection] = useState(null);

  // AI image generation transparency: the prompt the model actually received,
  // plus the user's corrections for the next attempt. Session-only — nothing
  // here is persisted with the menu item.
  const [showImagePromptDetails, setShowImagePromptDetails] = useState(false);
  const [lastImagePrompt, setLastImagePrompt] = useState('');
  const [imageFeedback, setImageFeedback] = useState('');
  const [usePreviousImage, setUsePreviousImage] = useState(true);
  
  // Refs for scrolling
  const formRef = useRef(null);
  const formActionsRef = useRef(null);
  const sectionRefs = {
    basicInfo: useRef(null),
    timing: useRef(null),
    tags: useRef(null),
    ingredients: useRef(null),
    instructions: useRef(null),
    equipment: useRef(null),
    nutrition: useRef(null)
  };
  const fieldRefs = useRef({});

  // Track scroll position for floating save button
  useEffect(() => {
    const handleScroll = () => {
      if (formActionsRef.current) {
        const rect = formActionsRef.current.getBoundingClientRect();
        // Show floating save when original save button is out of view
        setShowFloatingSave(rect.top > window.innerHeight || rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (initialData) {
      const prepTimeMinutes = extractMinutesAsString(initialData.prepTime);
      const cookTimeMinutes = extractMinutesAsString(initialData.cookTime);
      const totalMinutes = (parseInt(prepTimeMinutes) || 0) + (parseInt(cookTimeMinutes) || 0);

      setFormData(prev => ({
        ...prev,
        ...initialData,
        prepTime: prepTimeMinutes,
        cookTime: cookTimeMinutes,
        totalTime: totalMinutes > 0 ? formatMinutesToTime(totalMinutes) : (initialData.totalTime || ''),
        tags: initialData.tags || [],
        dietary: initialData.dietary || []
      }));
      if (initialData.image) {
        setPreview(initialData.image);
      }
    }
  }, [initialData]);

  // Fetch available tags, dietary options, and cuisines from the API
  useEffect(() => {
    const fetchFormOptions = async () => {
      try {
        const [dietaryResponse, cuisinesResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/menu/dietary-options`),
          fetch(`${process.env.REACT_APP_API_URL}/menu/cuisines`)
        ]);

        if (!dietaryResponse.ok) {
          throw new Error('Failed to fetch dietary options');
        }
        if (!cuisinesResponse.ok) {
          throw new Error('Failed to fetch cuisines');
        }

        const [dietary, cuisinesData] = await Promise.all([
          dietaryResponse.json(),
          cuisinesResponse.json()
        ]);

        const cuisines = Array.isArray(cuisinesData) ? cuisinesData : (cuisinesData.cuisines || []);
        setDietaryOptions(dietary.filter(Boolean));
        setCuisineOptions(cuisines.filter(Boolean).sort());
      } catch (error) {
        console.error('Error fetching form options:', error);
      }
    };

    fetchFormOptions();
  }, []);

  // Validation function
  const validateForm = useCallback(() => {
    const errors = {};
    let firstErrorSection = null;
    let firstErrorField = null;

    // Basic Info validation
    if (!formData.title?.trim()) {
      errors.title = 'Title is required';
      if (!firstErrorSection) { firstErrorSection = 'basicInfo'; firstErrorField = 'title'; }
    }
    if (!formData.description?.trim() && !formData.detailedDescription?.trim()) {
      errors.description = 'Either short or detailed description is required';
      if (!firstErrorSection) { firstErrorSection = 'basicInfo'; firstErrorField = 'description'; }
    }
    if (!formData.cuisine?.trim()) {
      errors.cuisine = 'Cuisine is required';
      if (!firstErrorSection) { firstErrorSection = 'basicInfo'; firstErrorField = 'cuisine'; }
    }

    // Timing validation
    if (!formData.prepTime?.trim()) {
      errors.prepTime = 'Prep time is required';
      if (!firstErrorSection) { firstErrorSection = 'timing'; firstErrorField = 'prepTime'; }
    }
    if (!formData.cookTime?.trim()) {
      errors.cookTime = 'Cook time is required';
      if (!firstErrorSection) { firstErrorSection = 'timing'; firstErrorField = 'cookTime'; }
    }
    // totalTime is auto-calculated, no validation needed
    if (!formData.servings || formData.servings < 1) {
      errors.servings = 'Servings must be at least 1';
      if (!firstErrorSection) { firstErrorSection = 'timing'; firstErrorField = 'servings'; }
    }

    // Ingredients validation
    const hasValidIngredient = formData.ingredients?.some(ing => ing.name?.trim());
    if (!hasValidIngredient) {
      errors.ingredients = 'At least one ingredient is required';
      if (!firstErrorSection) { firstErrorSection = 'ingredients'; firstErrorField = 'ingredients'; }
    }

    // Instructions validation
    const hasValidInstruction = formData.instructions?.some(inst => inst?.trim());
    if (!hasValidInstruction) {
      errors.instructions = 'At least one instruction is required';
      if (!firstErrorSection) { firstErrorSection = 'instructions'; firstErrorField = 'instructions'; }
    }

    return { errors, firstErrorSection, firstErrorField, isValid: Object.keys(errors).length === 0 };
  }, [formData]);

  // Scroll to error field and expand section
  const scrollToError = useCallback((section, field) => {
    // Collapse all sections except the error section
    const newCollapsed = {};
    Object.keys(SECTION_CONFIG).forEach(key => {
      newCollapsed[key] = key !== section;
    });
    setCollapsedSections(newCollapsed);
    setErrorSection(section);

    // Wait for DOM update then scroll
    setTimeout(() => {
      const fieldRef = fieldRefs.current[field];
      const sectionRef = sectionRefs[section]?.current;
      const targetRef = fieldRef || sectionRef;
      
      if (targetRef) {
        targetRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus the field if it's an input
        if (fieldRef?.focus) {
          fieldRef.focus();
        }
      }
    }, 100);
  }, [sectionRefs]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'contentUrl') {
      const isValidURL = validateUrl(value);
      setUrlError(!isValidURL && value !== '');
    }
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // Handle time inputs - numbers only
    if (name === 'prepTime' || name === 'cookTime') {
      let numericValue = value;
      if (!/^\d*$/.test(value)) {
        numericValue = String(parseTimeToMinutes(value) || '') || value.replace(/\D/g, '');
      }
      
      setTimeInputErrors(prev => ({ ...prev, [name]: '' }));
      
      // Auto-calculate totalTime when prepTime or cookTime changes
      setFormData(prev => {
        const newPrepTime = name === 'prepTime' ? numericValue : prev.prepTime;
        const newCookTime = name === 'cookTime' ? numericValue : prev.cookTime;
        
        const prepMinutes = parseInt(newPrepTime) || 0;
        const cookMinutes = parseInt(newCookTime) || 0;
        const totalMinutes = prepMinutes + cookMinutes;
        
        return {
          ...prev,
          [name]: numericValue,
          totalTime: formatMinutesToTime(totalMinutes)
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle cuisine selection
  const handleCuisineChange = (e) => {
    const value = e.target.value;
    if (value === '__new__') {
      setShowNewCuisineInput(true);
      setFormData(prev => ({ ...prev, cuisine: '' }));
    } else {
      setShowNewCuisineInput(false);
      setFormData(prev => ({ ...prev, cuisine: value }));
    }
    
    // Clear field error
    if (fieldErrors.cuisine) {
      setFieldErrors(prev => ({ ...prev, cuisine: null }));
    }
  };

  // Handle new cuisine input
  const handleNewCuisineInput = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, cuisine: value }));
    
    // Clear field error
    if (fieldErrors.cuisine) {
      setFieldErrors(prev => ({ ...prev, cuisine: null }));
    }
  };

  // Cancel new cuisine input
  const cancelNewCuisine = () => {
    setShowNewCuisineInput(false);
    setFormData(prev => ({ ...prev, cuisine: '' }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5242880) { // 5MB limit
        setError('Image file is too large. Please select an image less than 5MB in size.');
        e.target.value = '';
        return;
      }
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleIngredientChange = (index, field, value) => {
    // Clear ingredients error when editing
    if (fieldErrors.ingredients) {
      setFieldErrors(prev => ({ ...prev, ingredients: null }));
    }
    
    setFormData(prev => {
      const updatedIngredients = [...prev.ingredients];
      if (field === 'isSpice' || field === 'isOil') {
        updatedIngredients[index] = {
          ...updatedIngredients[index],
          [field]: !updatedIngredients[index][field],
          ...(field === 'isSpice' && value ? { isOil: false } : {}),
          ...(field === 'isOil' && value ? { isSpice: false } : {})
        };
      } else if (field === 'useFraction') {
        updatedIngredients[index] = {
          ...updatedIngredients[index],
          useFraction: !updatedIngredients[index].useFraction,
          quantity: '',
          numerator: '',
          denominator: ''
        };
      } else if (field === 'numerator' || field === 'denominator') {
        updatedIngredients[index] = {
          ...updatedIngredients[index],
          [field]: value,
          quantity: value && updatedIngredients[index][field === 'numerator' ? 'denominator' : 'numerator'] 
            ? (field === 'numerator' ? value : updatedIngredients[index].numerator) / 
              (field === 'denominator' ? value : updatedIngredients[index].denominator)
            : ''
        };
      } else {
        updatedIngredients[index] = {
          ...updatedIngredients[index],
          [field]: value
        };
      }
      return { ...prev, ingredients: updatedIngredients };
    });
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, {
        quantity: '',
        unit: '',
        name: '',
        prepNotes: '',
        isSpice: false,
        isOil: false,
        useFraction: false,
        numerator: '',
        denominator: ''
      }]
    }));
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleInstructionChange = (index, value) => {
    // Clear instructions error when editing
    if (fieldErrors.instructions) {
      setFieldErrors(prev => ({ ...prev, instructions: null }));
    }
    
    setFormData(prev => {
      const newInstructions = [...prev.instructions];
      newInstructions[index] = value;
      return {
        ...prev,
        instructions: newInstructions
      };
    });
  };

  const addInstruction = () => {
    setFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const handleEquipmentChange = (index, value) => {
    setFormData(prev => {
      const updated = [...prev.equipment];
      updated[index] = value;
      return { ...prev, equipment: updated };
    });
  };

  const addEquipment = () => {
    setFormData(prev => ({
      ...prev,
      equipment: [...prev.equipment, '']
    }));
  };

  const removeEquipment = (index) => {
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.filter((_, i) => i !== index)
    }));
  };

  const insertInstructionAt = (index) => {
    setFormData(prev => {
      const newInstructions = [...prev.instructions];
      newInstructions.splice(index, 0, '');
      return { ...prev, instructions: newInstructions };
    });
  };

  const removeInstruction = (index) => {
    setFormData(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const handleDietaryToggle = (option) => {
    setFormData(prev => ({ ...prev, dietary: toggleListValue(prev.dietary, option) }));
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({ ...prev, categories: toggleListValue(prev.categories, category) }));
  };

  const handleProteinToggle = (protein) => {
    setFormData(prev => ({ ...prev, proteins: toggleListValue(prev.proteins, protein) }));
  };

  const handleCookingMethodToggle = (method) => {
    setFormData((prev) => {
      const selected = itemCookingMethods(prev);
      const nextMethods = selected.includes(method)
        ? selected.filter((entry) => entry !== method)
        : [...selected, method];
      const preserved = (prev.tags || []).filter((tag) => !normalizeCookingMethod(tag));
      return { ...prev, tags: [...nextMethods, ...preserved] };
    });
  };

  const removeCustomTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: (prev.tags || []).filter((entry) => entry !== tag)
    }));
  };

  const addCustomTag = () => {
    const raw = customTagInput.trim().replace(/\s+/g, ' ');
    if (!raw) return;

    const normalized = raw.toLowerCase();
    if (isReservedFilterTag(normalized, dietaryOptions) || isMealTypeTag(normalized) || isProteinTag(normalized)) {
      toast.error('That belongs in Dietary, Categories, Protein, or Cooking Methods — pick it there instead.');
      return;
    }
    if (normalized.length > 30) {
      toast.error('Custom tags can be at most 30 characters.');
      return;
    }

    setFormData((prev) => {
      const currentOther = otherTagsFrom(prev.tags, dietaryOptions);
      if (currentOther.some((tag) => tag.toLowerCase() === normalized)) {
        return prev;
      }
      if (currentOther.length >= MAX_OTHER_TAGS) {
        toast.error(`You can add up to ${MAX_OTHER_TAGS} custom tags.`);
        return prev;
      }
      return { ...prev, tags: [...(prev.tags || []), normalized] };
    });
    setCustomTagInput('');
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setErrorSection(null);
    setSaveStatus('validating');

    // Validate form first
    const { errors, firstErrorSection, firstErrorField, isValid } = validateForm();
    
    if (!isValid) {
      setFieldErrors(errors);
      setSaveStatus('');
      
      // Build error message
      const errorMessages = Object.values(errors);
      setError(`Please fix the following: ${errorMessages.join(', ')}`);
      
      // Scroll to the first error
      if (firstErrorSection) {
        scrollToError(firstErrorSection, firstErrorField);
      }
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      let imageUrl = formData.image;

      // Upload image if selected
      if (imageFile) {
        setSaveStatus('uploading');
        const formDataImage = new FormData();
        formDataImage.append('image', imageFile);

        const uploadEndpoint = isAdmin ? 'admin/upload-menu-image' : 
                             isPersonal ? 'user/upload-menu-image' : 
                             'affiliate/upload-menu-image';

        const imageResponse = await fetch(`${process.env.REACT_APP_API_URL}/${uploadEndpoint}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataImage
        });

        if (!imageResponse.ok) {
          if (imageResponse.status === 413) {
            throw new Error('Image file is too large. Please upload an image less than 5MB in size.');
          }
          throw new Error('Failed to upload image');
        }

        const imageData = await imageResponse.json();
        imageUrl = imageData.imageUrl;
      }
      
      // Auto-generate image if none provided
      if (!imageUrl && formData.title) {
        setSaveStatus('generating');
        const generateEndpoint = isAdmin ? 'admin/generate-menu-image' : 
                                isPersonal ? 'user/generate-menu-image' : 
                                'affiliate/generate-menu-image';
        
        try {
          const genResponse = await fetch(`${process.env.REACT_APP_API_URL}/${generateEndpoint}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              title: formData.title,
              ingredients: formData.ingredients.map(ing => ing.name).filter(Boolean),
              description: formData.detailedDescription || formData.description || '',
              cuisine: formData.cuisine || '',
              feedback: imageFeedback.trim()
            })
          });

          const genData = await genResponse.json();

          if (genResponse.ok && genData.imageUrl) {
            imageUrl = genData.imageUrl;
          }
        } catch (genError) {
          console.log('Image generation failed, continuing without image:', genError);
        }
      }

      setSaveStatus('saving');

      // Format the data
      const formattedData = {
        ...formData,
        servings: Number(formData.servings),
        ingredients: formData.ingredients.map(ing => {
          let quantity;
          let quantityDisplay;
          if (ing.useFraction && ing.numerator && ing.denominator) {
            quantity = Number(ing.numerator) / Number(ing.denominator);
            // Keep what was typed so the recipe reads "1/3 cup" rather than 0.333.
            quantityDisplay = `${ing.numerator}/${ing.denominator}`;
          } else {
            quantity = ing.quantity ? Number(ing.quantity) : 1;
            quantityDisplay = toFraction(quantity);
          }
          return {
            ...ing,
            quantity,
            quantityDisplay
          };
        }),
        equipment: formData.equipment.filter(item => item.trim() !== ''),
        nutrition: {
          Calories: formData.nutrition.Calories || '0',
          Protein: formData.nutrition.Protein || '0',
          Carbohydrates: formData.nutrition.Carbohydrates || '0',
          Fat: formData.nutrition.Fat || '0',
          Fiber: formData.nutrition.Fiber || '0',
          Sodium: formData.nutrition.Sodium || '0'
        },
        image: imageUrl,
        categories: formData.categories || [],
        proteins: formData.proteins || [],
        tags: [
          ...itemCookingMethods(formData),
          ...otherTagsFrom(formData.tags, formData.dietary)
        ]
      };

      const customTagCount = otherTagsFrom(formData.tags, formData.dietary).length;
      if (customTagCount > MAX_OTHER_TAGS) {
        setSaveStatus('');
        setLoading(false);
        setError(`Please keep custom tags to ${MAX_OTHER_TAGS} or fewer (currently ${customTagCount}).`);
        scrollToError('tags');
        return;
      }

      let baseUrl = isAdmin ? 'admin' : (isPersonal ? 'user' : 'affiliate');
      let endpoint = isAdmin ? 'menu-items' : (isPersonal ? 'menus' : 'menu-items');
      
      const isUpdate = initialData && initialData._id;
      const url = isUpdate 
        ? `${process.env.REACT_APP_API_URL}/${baseUrl}/${endpoint}/${initialData._id}`
        : `${process.env.REACT_APP_API_URL}/${baseUrl}/${endpoint}`;

      const method = isUpdate ? 'PUT' : 'POST';

      const menuResponse = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formattedData)
      });

      if (!menuResponse.ok) {
        const errorData = await menuResponse.json();
        throw new Error(errorData.error || 'Failed to save menu item');
      }

      const menuData = await menuResponse.json();
      
      setSaveStatus('');
      onSuccess(menuData);
      
      if (!isUpdate) {
        setFormData(INITIAL_FORM_STATE);
        setImageFile(null);
        setPreview(null);
      }
    } catch (err) {
      setError(err.message);
      setSaveStatus('');
      // Scroll to top to show error
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } finally {
      setLoading(false);
    }
  };

  const validateUrl = (url) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // The ingredients the prompt will actually mention (the backend caps it at five).
  const imagePromptIngredients = formData.ingredients
    .map(ing => ing.name)
    .filter(Boolean)
    .slice(0, 5);

  // A local file preview is a blob URL the server can't fetch, so only an image
  // that already lives on our side can be corrected.
  const canUsePreviousImage = Boolean(formData.image) && !imageFile;

  const handleGenerateImage = async () => {
    if (!formData.title) {
      setError('Please enter a title before generating an image');
      return;
    }

    setIsGeneratingImage(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      const endpoint = isAdmin ? 'admin/generate-menu-image' :
                      isPersonal ? 'user/generate-menu-image' :
                      'affiliate/generate-menu-image';

      const referenceImageUrl = usePreviousImage && canUsePreviousImage ? formData.image : '';

      const response = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          ingredients: formData.ingredients.map(ing => ing.name).filter(Boolean),
          description: formData.detailedDescription || formData.description || '',
          cuisine: formData.cuisine || '',
          feedback: imageFeedback.trim(),
          referenceImageUrl
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate image');
      }

      setFormData(prev => ({ ...prev, image: data.imageUrl }));
      setPreview(data.imageUrl);
      setImageFile(null);
      setLastImagePrompt(data.prompt || '');

    } catch (err) {
      setError(err.message || 'Failed to generate image');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Get status message for save progress
  const getSaveStatusMessage = () => {
    switch (saveStatus) {
      case 'validating': return 'Validating form...';
      case 'uploading': return 'Uploading image...';
      case 'generating': return 'Generating image with AI (this may take a moment)...';
      case 'saving': return 'Saving menu item...';
      default: return '';
    }
  };

  // Check if a section has errors
  const sectionHasError = (sectionKey) => {
    const sectionFields = SECTION_CONFIG[sectionKey]?.fields || [];
    return sectionFields.some(field => fieldErrors[field]);
  };

  // Check if a section is complete (all required fields filled)
  const sectionIsComplete = (sectionKey) => {
    switch (sectionKey) {
      case 'basicInfo':
        return formData.title?.trim() && 
               (formData.description?.trim() || formData.detailedDescription?.trim()) && 
               formData.cuisine?.trim();
      case 'timing':
        return formData.prepTime?.trim() && 
               formData.cookTime?.trim() && 
               formData.totalTime?.trim() && 
               formData.servings >= 1;
      case 'tags':
        return (formData.tags?.length > 0)
          || (formData.dietary?.length > 0)
          || (formData.categories?.length > 0)
          || (formData.proteins?.length > 0);
      case 'ingredients':
        return formData.ingredients?.some(ing => ing.name?.trim());
      case 'instructions':
        return formData.instructions?.some(inst => inst?.trim());
      case 'nutrition':
        return true; // Nutrition is optional
      default:
        return false;
    }
  };

  // Render the save button content
  const renderSaveButton = (isFloating = false) => (
    <div className={`save-button-container ${isFloating ? 'floating' : ''}`}>
      {saveStatus && (
        <div className="save-status-indicator">
          <span className="save-spinner"></span>
          <span className="save-status-text">{getSaveStatusMessage()}</span>
        </div>
      )}
      {error && isFloating && (
        <div className="floating-error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}
      <div className="save-button-actions">
        {onCancel && (
          <button 
            type="button" 
            className="cancel-button" 
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button 
          type="submit" 
          className="submit-button" 
          disabled={loading}
          onClick={isFloating ? handleSubmit : undefined}
        >
          {loading ? (
            <>
              <span className="button-spinner"></span>
              {getSaveStatusMessage() || 'Saving...'}
            </>
          ) : (
            initialData && initialData._id ? 'Update Menu Item' : 'Save Menu'
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="menu-upload-form" ref={formRef}>
      <div className="form-header">
        <h2>{initialData && initialData._id ? 'Edit Menu Item' : 'New Menu Item'}</h2>
        {onCancel && (
          <button 
            type="button" 
            className="form-close-btn" 
            onClick={onCancel}
            title="Close without saving"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>
      
      {error && (
        <div className="error-message sticky-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
          <button type="button" className="error-dismiss" onClick={() => setError(null)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}
        <div 
          className={`form-section ${collapsedSections.basicInfo ? 'collapsed' : ''} ${sectionHasError('basicInfo') ? 'has-error' : ''} ${sectionIsComplete('basicInfo') ? 'is-complete' : ''}`}
          ref={sectionRefs.basicInfo}
        >
          <div className="section-header" onClick={() => toggleSection('basicInfo')}>
            <h3>
              {sectionIsComplete('basicInfo') && <i className="fas fa-check-circle section-complete-icon"></i>}
              {sectionHasError('basicInfo') && <i className="fas fa-exclamation-circle section-error-icon"></i>}
              Basic Information
            </h3>
            <button type="button" className="section-toggle">
              <i className={`fas fa-chevron-${collapsedSections.basicInfo ? 'down' : 'up'}`}></i>
            </button>
          </div>
          
          <div className="section-content">
            <div className={`form-group ${fieldErrors.title ? 'has-error' : ''}`}>
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                ref={el => fieldRefs.current.title = el}
                className={fieldErrors.title ? 'error-input' : ''}
              />
              {fieldErrors.title && <span className="field-error">{fieldErrors.title}</span>}
            </div>

            <div className={`form-group ${fieldErrors.description ? 'has-error' : ''}`}>
              <label htmlFor="description">Short Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                ref={el => fieldRefs.current.description = el}
                className={fieldErrors.description ? 'error-input' : ''}
              />
              {fieldErrors.description && <span className="field-error">{fieldErrors.description}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="detailedDescription">Detailed Description</label>
              <textarea
                id="detailedDescription"
                name="detailedDescription"
                value={formData.detailedDescription}
                onChange={handleInputChange}
              />
            </div>

            <div className={`form-group ${fieldErrors.cuisine ? 'has-error' : ''}`}>
              <label htmlFor="cuisine">Cuisine *</label>
              {showNewCuisineInput ? (
                <div className="new-cuisine-input-container">
                  <input
                    type="text"
                    id="cuisine"
                    name="cuisine"
                    value={formData.cuisine}
                    onChange={handleNewCuisineInput}
                    placeholder="Enter new cuisine name..."
                    ref={el => fieldRefs.current.cuisine = el}
                    className={fieldErrors.cuisine ? 'error-input' : ''}
                    autoFocus
                  />
                  <button 
                    type="button" 
                    className="cancel-new-cuisine-btn"
                    onClick={cancelNewCuisine}
                    title="Cancel and select from existing"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <select
                  id="cuisine"
                  name="cuisine"
                  value={formData.cuisine}
                  onChange={handleCuisineChange}
                  ref={el => fieldRefs.current.cuisine = el}
                  className={fieldErrors.cuisine ? 'error-input' : ''}
                >
                  <option value="">Select a cuisine...</option>
                  {cuisineOptions.map(cuisine => (
                    <option key={cuisine} value={cuisine}>{cuisine}</option>
                  ))}
                  <option value="__new__">+ Add new cuisine...</option>
                </select>
              )}
              {fieldErrors.cuisine && <span className="field-error">{fieldErrors.cuisine}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="contentUrl">Content URL</label>
              <div className="url-input-container">
                <input
                  type="url"
                  id="contentUrl"
                  name="contentUrl"
                  value={formData.contentUrl}
                  onChange={handleInputChange}
                  placeholder="Enter recipe URL..."
                  className={urlError ? 'error' : ''}
                />
                {formData.contentUrl && !urlError && (
                  <i className="fas fa-check validation-icon success"></i>
                )}
                {urlError && (
                  <i className="fas fa-exclamation-circle validation-icon error"></i>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="image">Menu Image</label>
              {preview && (
                <div className="image-preview">
                  <img src={preview} alt="Preview" />
                  <button 
                    type="button" 
                    className="remove-image-button"
                    onClick={() => {
                      setPreview(null);
                      setImageFile(null);
                      setFormData(prev => ({ ...prev, image: '' }));
                    }}
                  >
                    Remove Image
                  </button>
                </div>
              )}
              <div className="image-upload-options">
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <span className="image-option-separator">or</span>
                <button
                  type="button"
                  className="generate-image-button"
                  onClick={handleGenerateImage}
                  disabled={isGeneratingImage || !formData.title}
                  title={!formData.title ? 'Enter a title first to generate an image' : 'Generate an AI image from the title, description, cuisine and ingredients'}
                >
                  {isGeneratingImage ? (
                    <>
                      <span className="button-spinner"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic"></i>
                      Generate Image
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className={`image-prompt-details-toggle ${showImagePromptDetails ? 'is-open' : ''}`}
                  onClick={() => setShowImagePromptDetails(prev => !prev)}
                  aria-expanded={showImagePromptDetails}
                  title="See what gets sent to the image model, and tell it what to fix"
                >
                  <i className="fas fa-circle-info"></i>
                  Details
                </button>
              </div>

              {showImagePromptDetails && (
                <div className="image-prompt-details">
                  <div className="image-prompt-block">
                    <h4>What the image model is told</h4>
                    <dl className="image-prompt-inputs">
                      <dt>Title</dt>
                      <dd>{formData.title || <span className="image-prompt-missing">Not set</span>}</dd>
                      <dt>Cuisine</dt>
                      <dd>{formData.cuisine || <span className="image-prompt-missing">Not set</span>}</dd>
                      <dt>Description</dt>
                      <dd>
                        {formData.detailedDescription || formData.description || (
                          <span className="image-prompt-missing">Not set</span>
                        )}
                      </dd>
                      <dt>Ingredients</dt>
                      <dd>
                        {imagePromptIngredients.length > 0 ? (
                          imagePromptIngredients.join(', ')
                        ) : (
                          <span className="image-prompt-missing">None yet</span>
                        )}
                      </dd>
                    </dl>
                    <p className="image-prompt-hint">
                      Only the first five ingredients are used. Camera angle and lighting are picked
                      at random each time, so the exact wording differs from one attempt to the next.
                    </p>
                  </div>

                  {lastImagePrompt && (
                    <div className="image-prompt-block">
                      <h4>Exact prompt used for the current image</h4>
                      <pre className="image-prompt-text">{lastImagePrompt}</pre>
                    </div>
                  )}

                  <div className="image-prompt-block">
                    <h4>Not right? Say what to change</h4>
                    <textarea
                      className="image-feedback-input"
                      value={imageFeedback}
                      onChange={(e) => setImageFeedback(e.target.value.slice(0, 500))}
                      placeholder="e.g. There is no egg in this dish — remove the egg yolk and show chewy cylindrical rice cakes instead of pasta."
                      rows={3}
                    />
                    <div className="image-feedback-meta">
                      <span>{imageFeedback.length}/500</span>
                    </div>
                    <label className="image-feedback-checkbox">
                      <input
                        type="checkbox"
                        checked={usePreviousImage}
                        onChange={(e) => setUsePreviousImage(e.target.checked)}
                        disabled={!canUsePreviousImage}
                      />
                      <span>
                        Correct the current image instead of starting over
                        {!canUsePreviousImage && ' (needs a generated image first)'}
                      </span>
                    </label>
                    <button
                      type="button"
                      className="generate-image-button regenerate"
                      onClick={handleGenerateImage}
                      disabled={isGeneratingImage || !formData.title || !imageFeedback.trim()}
                    >
                      {isGeneratingImage ? (
                        <>
                          <span className="button-spinner"></span>
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-rotate"></i>
                          Regenerate with these notes
                        </>
                      )}
                    </button>
                    <p className="image-prompt-hint">
                      These notes apply to this editing session only. They are sent as hard
                      requirements that override the dish name, which is what usually drags the
                      image toward a familiar-but-wrong version of the recipe.
                    </p>
                  </div>
                </div>
              )}
              {!preview && !formData.image && (
                <div className="image-auto-generate-notice">
                  <i className="fas fa-info-circle"></i>
                  <span>
                    <strong>No image?</strong> If you don't upload an image, one will be automatically 
                    generated using AI based on the recipe title and ingredients when you save. 
                    You can remove this and upload your own image at any time.
                  </span>
                </div>
              )}
              {!preview && formData.image && (
                <div className="current-image">
                  <p>Current image URL: {formData.image}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timing & Servings Section */}
        <div 
          className={`form-section ${collapsedSections.timing ? 'collapsed' : ''} ${sectionHasError('timing') ? 'has-error' : ''} ${sectionIsComplete('timing') ? 'is-complete' : ''}`}
          ref={sectionRefs.timing}
        >
          <div className="section-header" onClick={() => toggleSection('timing')}>
            <h3>
              {sectionIsComplete('timing') && <i className="fas fa-check-circle section-complete-icon"></i>}
              {sectionHasError('timing') && <i className="fas fa-exclamation-circle section-error-icon"></i>}
              Timing & Servings
            </h3>
            <button type="button" className="section-toggle">
              <i className={`fas fa-chevron-${collapsedSections.timing ? 'down' : 'up'}`}></i>
            </button>
          </div>
          
          <div className="section-content">
            <div className="form-row">
              <div className={`form-group ${fieldErrors.prepTime ? 'has-error' : ''} ${timeInputErrors.prepTime ? 'has-error' : ''}`}>
                <label htmlFor="prepTime">Prep Time *</label>
                <div className="time-input-container">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    id="prepTime"
                    name="prepTime"
                    value={formData.prepTime}
                    onChange={handleInputChange}
                    placeholder="15"
                    ref={el => fieldRefs.current.prepTime = el}
                    className={`time-input ${fieldErrors.prepTime || timeInputErrors.prepTime ? 'error-input' : ''}`}
                  />
                  <span className="time-unit">minutes</span>
                </div>
                {fieldErrors.prepTime && <span className="field-error">{fieldErrors.prepTime}</span>}
                {timeInputErrors.prepTime && <span className="field-error time-error">{timeInputErrors.prepTime}</span>}
              </div>

              <div className={`form-group ${fieldErrors.cookTime ? 'has-error' : ''} ${timeInputErrors.cookTime ? 'has-error' : ''}`}>
                <label htmlFor="cookTime">Cook Time *</label>
                <div className="time-input-container">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    id="cookTime"
                    name="cookTime"
                    value={formData.cookTime}
                    onChange={handleInputChange}
                    placeholder="30"
                    ref={el => fieldRefs.current.cookTime = el}
                    className={`time-input ${fieldErrors.cookTime || timeInputErrors.cookTime ? 'error-input' : ''}`}
                  />
                  <span className="time-unit">minutes</span>
                </div>
                {fieldErrors.cookTime && <span className="field-error">{fieldErrors.cookTime}</span>}
                {timeInputErrors.cookTime && <span className="field-error time-error">{timeInputErrors.cookTime}</span>}
              </div>

              <div className="form-group total-time-group">
                <label htmlFor="totalTime">Total Time</label>
                <input
                  type="text"
                  id="totalTime"
                  name="totalTime"
                  value={formData.totalTime}
                  readOnly
                  disabled
                  placeholder="Auto-calculated"
                  className="total-time-input"
                />
              </div>

              <div className={`form-group ${fieldErrors.servings ? 'has-error' : ''}`}>
                <label htmlFor="servings">Servings *</label>
                <input
                  type="number"
                  id="servings"
                  name="servings"
                  value={formData.servings}
                  onChange={handleInputChange}
                  min="1"
                  ref={el => fieldRefs.current.servings = el}
                  className={fieldErrors.servings ? 'error-input' : ''}
                />
                {fieldErrors.servings && <span className="field-error">{fieldErrors.servings}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Ingredients Section */}
        <div 
          className={`form-section ${collapsedSections.ingredients ? 'collapsed' : ''} ${sectionHasError('ingredients') ? 'has-error' : ''} ${sectionIsComplete('ingredients') ? 'is-complete' : ''}`}
          ref={sectionRefs.ingredients}
        >
          <div className="section-header" onClick={() => toggleSection('ingredients')}>
            <h3>
              {sectionIsComplete('ingredients') && <i className="fas fa-check-circle section-complete-icon"></i>}
              {sectionHasError('ingredients') && <i className="fas fa-exclamation-circle section-error-icon"></i>}
              Ingredients
            </h3>
            <button type="button" className="section-toggle">
              <i className={`fas fa-chevron-${collapsedSections.ingredients ? 'down' : 'up'}`}></i>
            </button>
          </div>
          
          <div className="section-content">
            {fieldErrors.ingredients && (
              <div className="section-error-notice">
                <i className="fas fa-exclamation-circle"></i>
                {fieldErrors.ingredients}
              </div>
            )}
            {formData.ingredients.map((ingredient, index) => (
              <div key={index} className="ingredient-row">
                <div className="quantity-input-group">
                  <label>Quantity *</label>
                  <div className="quantity-with-toggle">
                    {ingredient.useFraction ? (
                      <div className="fraction-inputs">
                        <input
                          type="number"
                          value={ingredient.numerator}
                          onChange={(e) => handleIngredientChange(index, 'numerator', e.target.value)}
                          placeholder="Num"
                          min="0"
                        />
                        <span>/</span>
                        <input
                          type="number"
                          value={ingredient.denominator}
                          onChange={(e) => handleIngredientChange(index, 'denominator', e.target.value)}
                          placeholder="Den"
                          min="1"
                        />
                      </div>
                    ) : (
                      <input
                        type="number"
                        value={ingredient.quantity}
                        onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    )}
                    <button
                      type="button"
                      className="toggle-fraction-btn"
                      onClick={() => handleIngredientChange(index, 'useFraction')}
                      title={ingredient.useFraction ? "Input decimal" : "Input fraction"}
                    >
                      {ingredient.useFraction ? '123' : '1/2'}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <input
                    type="text"
                    value={ingredient.unit}
                    onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                    placeholder="e.g., cup, tbsp"
                  />
                </div>
                <div className="form-group">
                  <label>Ingredient Name*</label>
                  <input
                    type="text"
                    value={ingredient.name}
                    onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                    placeholder="e.g., butter"
                  />
                </div>
                <div className="form-group">
                  <label>Prep Notes</label>
                  <input
                    type="text"
                    value={ingredient.prepNotes}
                    onChange={(e) => handleIngredientChange(index, 'prepNotes', e.target.value)}
                    placeholder="e.g., finely chopped"
                  />
                </div>
                <div className="ingredient-type-checkboxes">
                  <label>
                    <input
                      type="checkbox"
                      checked={ingredient.isSpice}
                      onChange={(e) => handleIngredientChange(index, 'isSpice', e.target.checked)}
                    />
                    Spice
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={ingredient.isOil}
                      onChange={(e) => handleIngredientChange(index, 'isOil', e.target.checked)}
                    />
                    Oil
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => removeIngredient(index)}
                  className="remove-button"
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addIngredient} className="add-button">
              Add Ingredient
            </button>
          </div>
        </div>

        {/* Instructions Section */}
        <div 
          className={`form-section ${collapsedSections.instructions ? 'collapsed' : ''} ${sectionHasError('instructions') ? 'has-error' : ''} ${sectionIsComplete('instructions') ? 'is-complete' : ''}`}
          ref={sectionRefs.instructions}
        >
          <div className="section-header" onClick={() => toggleSection('instructions')}>
            <h3>
              {sectionIsComplete('instructions') && <i className="fas fa-check-circle section-complete-icon"></i>}
              {sectionHasError('instructions') && <i className="fas fa-exclamation-circle section-error-icon"></i>}
              Instructions
            </h3>
            <button type="button" className="section-toggle">
              <i className={`fas fa-chevron-${collapsedSections.instructions ? 'down' : 'up'}`}></i>
            </button>
          </div>
          
          <div className="section-content">
            {fieldErrors.instructions && (
              <div className="section-error-notice">
                <i className="fas fa-exclamation-circle"></i>
                {fieldErrors.instructions}
              </div>
            )}
            {formData.instructions.map((instruction, index) => (
              <div key={index} className="instruction-wrapper">
                <div className="instruction-row">
                  <div className="instruction-header">
                    <div className="instruction-number">
                      <span>{index + 1}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeInstruction(index)}
                      className="instruction-remove-button"
                      aria-label={`Remove step ${index + 1}`}
                      title={`Remove step ${index + 1}`}
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true"></i>
                    </button>
                  </div>
                  <div className="form-group instruction-textarea-group">
                    <textarea
                      value={instruction}
                      onChange={(e) => handleInstructionChange(index, e.target.value)}
                      placeholder={`Step ${index + 1}`}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => insertInstructionAt(index + 1)}
                  className="insert-instruction-btn"
                  title="Insert step here"
                >
                  <i className="fas fa-plus"></i> Insert step below
                </button>
              </div>
            ))}
            {formData.instructions.length === 0 && (
              <button type="button" onClick={addInstruction} className="add-button">
                Add First Instruction
              </button>
            )}
          </div>
        </div>

        {/* Kitchen Equipment Section */}
        <div 
          className={`form-section ${collapsedSections.equipment ? 'collapsed' : ''} ${sectionIsComplete('equipment') ? 'is-complete' : ''}`}
          ref={sectionRefs.equipment}
        >
          <div className="section-header" onClick={() => toggleSection('equipment')}>
            <h3>
              {sectionIsComplete('equipment') && <i className="fas fa-check-circle section-complete-icon"></i>}
              <i className="fas fa-utensils"></i> Kitchen Equipment
              <span className="optional-badge">Optional</span>
            </h3>
            <button type="button" className="section-toggle">
              <i className={`fas fa-chevron-${collapsedSections.equipment ? 'down' : 'up'}`}></i>
            </button>
          </div>
          
          <div className="section-content">
            {formData.equipment.map((item, index) => (
              <div key={index} className="equipment-row">
                <div className="form-group">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleEquipmentChange(index, e.target.value)}
                    placeholder="e.g., Wok, Mixing bowls, Baking sheet"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeEquipment(index)}
                  className="remove-button"
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addEquipment} className="add-button">
              Add Equipment
            </button>
          </div>
        </div>

        {/* Tags Section */}
        <div 
          className={`form-section ${collapsedSections.tags ? 'collapsed' : ''} ${sectionIsComplete('tags') ? 'is-complete' : ''}`}
          ref={sectionRefs.tags}
        >
          <div className="section-header" onClick={() => toggleSection('tags')}>
            <h3>
              {sectionIsComplete('tags') && <i className="fas fa-check-circle section-complete-icon"></i>}
              Tags & Dietary Preferences
              <span className="optional-badge">Optional</span>
            </h3>
            <button type="button" className="section-toggle">
              <i className={`fas fa-chevron-${collapsedSections.tags ? 'down' : 'up'}`}></i>
            </button>
          </div>
          
          <div className="section-content">
            <div className="tags-selection-container">
              <p className="tags-help-text">
                <i className="fas fa-info-circle"></i>
                These sections match the browse filters. Use custom tags only for extras that do not fit Dietary, Categories, Protein, or Cooking Methods ({MAX_OTHER_TAGS} max).
              </p>

              {(formData.dietary?.length > 0
                || formData.categories?.length > 0
                || formData.proteins?.length > 0
                || itemCookingMethods(formData).length > 0
                || otherTagsFrom(formData.tags, formData.dietary).length > 0) && (
                <div className="selected-tags-display">
                  <label>Selected ({
                    (formData.dietary?.length || 0)
                    + (formData.categories?.length || 0)
                    + (formData.proteins?.length || 0)
                    + itemCookingMethods(formData).length
                    + otherTagsFrom(formData.tags, formData.dietary).length
                  }):</label>
                  <div className="selected-tags-chips">
                    {formData.dietary?.map((option) => (
                      <span key={`dietary-${option}`} className="selected-tag-chip dietary-chip">
                        <i className="fas fa-leaf"></i>
                        {option}
                        <button type="button" onClick={() => handleDietaryToggle(option)} className="remove-tag-btn">
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                    {formData.categories?.map((category) => (
                      <span key={`category-${category}`} className="selected-tag-chip">
                        {MEAL_TYPE_LABELS[category] || category}
                        <button type="button" onClick={() => handleCategoryToggle(category)} className="remove-tag-btn">
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                    {formData.proteins?.map((protein) => (
                      <span key={`protein-${protein}`} className="selected-tag-chip">
                        {PROTEIN_LABELS[protein] || protein}
                        <button type="button" onClick={() => handleProteinToggle(protein)} className="remove-tag-btn">
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                    {itemCookingMethods(formData).map((method) => (
                      <span key={`method-${method}`} className="selected-tag-chip">
                        {COOKING_METHOD_LABELS[method] || method}
                        <button type="button" onClick={() => handleCookingMethodToggle(method)} className="remove-tag-btn">
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                    {otherTagsFrom(formData.tags, formData.dietary).map((tag) => (
                      <span key={`tag-${tag}`} className="selected-tag-chip">
                        {tag}
                        <button type="button" onClick={() => removeCustomTag(tag)} className="remove-tag-btn">
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {dietaryOptions.length > 0 && (
                <div className="tag-category dietary-category">
                  <h4>
                    <i className="fas fa-leaf"></i>
                    Dietary Preferences
                  </h4>
                  <div className="tag-chips">
                    {dietaryOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`tag-chip dietary ${formData.dietary?.includes(option) ? 'selected' : ''}`}
                        onClick={() => handleDietaryToggle(option)}
                      >
                        {formData.dietary?.includes(option) && <i className="fas fa-check"></i>}
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="tag-category">
                <h4>
                  <i className="fas fa-fire"></i>
                  Cooking Methods
                </h4>
                <div className="tag-chips">
                  {COOKING_METHODS.map((method) => (
                    <button
                      key={method}
                      type="button"
                      className={`tag-chip ${itemCookingMethods(formData).includes(method) ? 'selected' : ''}`}
                      onClick={() => handleCookingMethodToggle(method)}
                    >
                      {itemCookingMethods(formData).includes(method) && <i className="fas fa-check"></i>}
                      {COOKING_METHOD_LABELS[method]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="tag-category">
                <h4>
                  <i className="fas fa-utensils"></i>
                  Categories
                </h4>
                <div className="tag-chips">
                  {MEAL_TYPE_CATEGORIES.map((category) => (
                    <button
                      key={category}
                      type="button"
                      className={`tag-chip ${formData.categories?.includes(category) ? 'selected' : ''}`}
                      onClick={() => handleCategoryToggle(category)}
                    >
                      {formData.categories?.includes(category) && <i className="fas fa-check"></i>}
                      {MEAL_TYPE_LABELS[category]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="tag-category">
                <h4>
                  <i className="fas fa-drumstick-bite"></i>
                  Protein
                </h4>
                <div className="tag-chips">
                  {PROTEINS.map((protein) => (
                    <button
                      key={protein}
                      type="button"
                      className={`tag-chip ${formData.proteins?.includes(protein) ? 'selected' : ''}`}
                      onClick={() => handleProteinToggle(protein)}
                    >
                      {formData.proteins?.includes(protein) && <i className="fas fa-check"></i>}
                      {PROTEIN_LABELS[protein]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="tag-category">
                <h4>
                  <i className="fas fa-tag"></i>
                  Other Tags
                  <span className="tag-limit-note">
                    {otherTagsFrom(formData.tags, formData.dietary).length}/{MAX_OTHER_TAGS}
                  </span>
                </h4>
                {otherTagsFrom(formData.tags, formData.dietary).length > MAX_OTHER_TAGS && (
                  <p className="tag-limit-warning">
                    This recipe has more than {MAX_OTHER_TAGS} custom tags. Remove extras before saving.
                  </p>
                )}
                <div className="tag-chips">
                  {otherTagsFrom(formData.tags, formData.dietary).map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className="tag-chip selected"
                      onClick={() => removeCustomTag(tag)}
                    >
                      <i className="fas fa-check"></i>
                      {tag}
                    </button>
                  ))}
                </div>
                <div className="custom-tag-row">
                  <input
                    type="text"
                    className="custom-tag-input"
                    maxLength={30}
                    placeholder="Add a custom tag (e.g. one-pot)"
                    value={customTagInput}
                    disabled={otherTagsFrom(formData.tags, formData.dietary).length >= MAX_OTHER_TAGS}
                    onChange={(e) => setCustomTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomTag();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="custom-tag-add-btn"
                    onClick={addCustomTag}
                    disabled={otherTagsFrom(formData.tags, formData.dietary).length >= MAX_OTHER_TAGS}
                  >
                    Add tag
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Nutrition Section */}
        <div 
          className={`form-section ${collapsedSections.nutrition ? 'collapsed' : ''} ${sectionIsComplete('nutrition') ? 'is-complete' : ''}`}
          ref={sectionRefs.nutrition}
        >
          <div className="section-header" onClick={() => toggleSection('nutrition')}>
            <h3>
              <i className="fas fa-check-circle section-complete-icon" style={{ opacity: 0.5 }}></i>
              Nutrition Information
              <span className="optional-badge">Optional</span>
            </h3>
            <button type="button" className="section-toggle">
              <i className={`fas fa-chevron-${collapsedSections.nutrition ? 'down' : 'up'}`}></i>
            </button>
          </div>
          
          <div className="section-content">
            <div className="nutrition-grid">
              {Object.keys(formData.nutrition).map(nutrient => {
                const unitMap = {
                  Calories: { unit: 'kcal', placeholder: 'e.g., 350' },
                  Protein: { unit: 'g', placeholder: 'e.g., 25' },
                  Carbohydrates: { unit: 'g', placeholder: 'e.g., 40' },
                  Fat: { unit: 'g', placeholder: 'e.g., 12' },
                  Fiber: { unit: 'g', placeholder: 'e.g., 5' },
                  Sodium: { unit: 'mg', placeholder: 'e.g., 480' }
                };
                const info = unitMap[nutrient] || { unit: '', placeholder: 'e.g., 0' };
                return (
                  <div key={nutrient} className="form-group">
                    <label htmlFor={nutrient}>{nutrient} <span className="unit-label">({info.unit})</span></label>
                    <input
                      type="text"
                      inputMode="decimal"
                      id={nutrient}
                      value={formData.nutrition[nutrient]}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\d.]/g, '').replace(/(\..*?)\./g, '$1');
                        setFormData(prev => ({
                          ...prev,
                          nutrition: {
                            ...prev.nutrition,
                            [nutrient]: val
                          }
                        }));
                      }}
                      placeholder={info.placeholder}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions" ref={formActionsRef}>
          {renderSaveButton(false)}
        </div>
      </form>

      {/* Floating Save Button */}
      {showFloatingSave && (
        <div className="floating-save-container">
          {renderSaveButton(true)}
        </div>
      )}
    </div>
  );
};

export default MenuForm;